import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { API_BASE } from './config';
import '../css/JoinCheckin.css';

const avatarSource = value => {
  if (!value) return '';
  return /^https?:|^data:|^blob:/i.test(value)
    ? value
    : `${API_BASE}${value.startsWith('/') ? '' : '/'}${value}`;
};

export default function JoinCheckin() {
  const { groupId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const code = useMemo(() => new URLSearchParams(location.search).get('code') || '', [location.search]);
  const qrPath = `/join/${groupId}?code=${encodeURIComponent(code)}`;

  const [step, setStep] = useState('LOADING');
  const [group, setGroup] = useState(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [message, setMessage] = useState('');
  const [checkingIn, setCheckingIn] = useState(false);
  const pendingStorageKey = `pending_qr_checkin_${groupId}`;

  const request = async (path, options = {}) => {
    const response = await fetch(`${API_BASE}${path}`, options);
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.message || 'ดำเนินการไม่สำเร็จ');
    return data;
  };

  useEffect(() => {
    let active = true;
    const start = async () => {
      try {
        const info = await request(`/api/checkin/join/${groupId}/info?code=${encodeURIComponent(code)}`);
        if (!active) return;
        setGroup(info);
        const pendingUser = location.state?.pendingCheckinUser || (() => {
          try { return JSON.parse(localStorage.getItem(pendingStorageKey) || 'null'); }
          catch { return null; }
        })();
        if (pendingUser) {
          setSelected(pendingUser);
          setStep('REGISTERED');
          window.history.replaceState({}, '', qrPath);
          return;
        }
        if (location.state?.checkinUser) {
          setSelected(location.state.checkinUser);
          setStep('SUCCESS');
          window.history.replaceState({}, '', qrPath);
          return;
        }
        const deviceToken = localStorage.getItem('device_token');
        if (!deviceToken) return setStep('SEARCH');
        try {
          const data = await request(`/api/checkin/join/${groupId}/device`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${deviceToken}` },
            body: JSON.stringify({ code })
          });
          if (!active) return;
          localStorage.setItem('user', JSON.stringify(data.user));
          localStorage.setItem('last_group_id', String(groupId));
          setSelected(data.user);
          setStep('SUCCESS');
        } catch {
          localStorage.removeItem('device_token');
          if (active) setStep('SEARCH');
        }
      } catch (error) {
        if (active) { setMessage(error.message); setStep('ERROR'); }
      }
    };
    start();
    return () => { active = false; };
  }, [code, groupId, location.state, pendingStorageKey, qrPath]);

  const checkInToday = async () => {
    if (checkingIn) return;
    try {
      setCheckingIn(true);
      setMessage('');
      const deviceToken = localStorage.getItem('device_token');
      if (!deviceToken) throw new Error('ไม่พบข้อมูลอุปกรณ์ กรุณา Login ใหม่');
      const data = await request(`/api/checkin/join/${groupId}/device`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${deviceToken}` },
        body: JSON.stringify({ code })
      });
      localStorage.removeItem(pendingStorageKey);
      localStorage.setItem('user', JSON.stringify(data.user));
      localStorage.setItem('last_group_id', String(groupId));
      setSelected(data.user);
      setStep('SUCCESS');
    } catch (error) {
      setMessage(error.message);
    } finally {
      setCheckingIn(false);
    }
  };

  useEffect(() => {
    if (step !== 'SEARCH' || !query.trim()) { setResults([]); return undefined; }
    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      try {
        const data = await request(
          `/api/checkin/join/${groupId}/search?code=${encodeURIComponent(code)}&q=${encodeURIComponent(query.trim())}`,
          { signal: controller.signal }
        );
        setResults(data.results || []);
      } catch (error) {
        if (error.name !== 'AbortError') setMessage(error.message);
      }
    }, 220);
    return () => { window.clearTimeout(timer); controller.abort(); };
  }, [code, groupId, query, step]);

  return (
    <main className="join-page">
      <section className="join-card">
        <div className="join-mark">🏸</div>
        <small>QR CHECK-IN</small>
        <h1>{group?.Group_Name || 'เช็คอินเข้าสนาม'}</h1>

        {step === 'LOADING' && <p>กำลังตรวจสอบอุปกรณ์…</p>}
        {step === 'ERROR' && <div className="join-error">{message}</div>}

        {step === 'SEARCH' && <>
          <h2>ค้นหาสมาชิก</h2>
          <p>ค้นหาด้วยชื่อ หรือ Member ID</p>
          <input className="join-search" autoFocus value={query} onChange={e => setQuery(e.target.value)} placeholder="🔎 ชื่อสมาชิก / Member ID" />
          <div className="join-results">
            {results.map(player => (
              <button type="button" key={player.user_id} onClick={() => navigate(
                `/join/${groupId}/login?code=${encodeURIComponent(code)}`,
                { state: { player, groupName: group?.Group_Name } }
              )}>
                <span className="join-avatar">
                  {player.avatar_url ? <img src={avatarSource(player.avatar_url)} alt="" /> : player.name?.charAt(0)}
                </span>
                <span><strong>{player.name}</strong><small>Member #{player.user_id} • Rank {player.level_rank || '-'}</small></span>
                <b>เลือก</b>
              </button>
            ))}
            {query.trim() && !results.length && <p className="join-empty">ไม่พบรายชื่อที่ตรงกัน</p>}
          </div>
          <button className="join-secondary" type="button" onClick={() => navigate(`/register?redirect=${encodeURIComponent(qrPath)}&groupId=${groupId}&code=${encodeURIComponent(code)}&qrCheckin=1`)}>
            หาไม่เจอ / สมัครสมาชิก
          </button>
        </>}

        {step === 'REGISTERED' && <div className="join-success join-pending">
          <div>✓</div><h2>สมัครสมาชิกสำเร็จ</h2>
          <p><strong>{selected?.name}</strong> อยู่ใน {group?.Group_Name} แล้ว</p>
          <span>สถานะ HOLD • ยังไม่ได้เช็คอิน</span>
          {message && <div className="join-error">{message}</div>}
          <button className="join-primary" type="button" disabled={checkingIn} onClick={checkInToday}>
            {checkingIn ? 'กำลังเช็คอิน…' : '🏸 เช็คอินตีแบดวันนี้'}
          </button>
          <button className="join-secondary" type="button" onClick={() => navigate(`/monitor/${groupId}`)}>ดู Match Monitor</button>
        </div>}

        {step === 'SUCCESS' && <div className="join-success">
          <div>✓</div><h2>Check-in Success</h2>
          <p><strong>{selected?.name}</strong> เข้าร่วม {group?.Group_Name} แล้ว</p>
          <span>สถานะ ACTIVE</span>
          <button className="join-primary" type="button" onClick={() => navigate(`/monitor/${groupId}`)}>ดู Match Monitor</button>
        </div>}
      </section>
    </main>
  );
}
